import { ClassNames, DateRange, DayPicker, useInput } from 'react-day-picker';
import clsx from 'clsx';
import { FieldError } from 'react-hook-form';
import { CheckIcon } from '@snapi/icons';
import { useClickAway, useToggle } from 'react-use';
import { AnimatePresence, motion } from 'framer-motion';
import { format, isAfter } from 'date-fns';
import { useRef } from 'react';

interface BithdayPickerProps {
  label?: string;
  horizontal?: boolean;
  valid?: boolean;
  disabled?: boolean;
  dob: string;
  onChange(dob: string): void;
  error?: FieldError;
}

const classNames: ClassNames = {
  months: 'flex flex-1',
  month: 'flex flex-1 flex-col',
  table: 'flex flex-1 self-center flex-col',
  head: 'flex flex-1',
  head_cell: 'w-8 h-8 sm:w-9 sm:h-9 p-0.5 text-[13px] font-light',
  caption: 'flex',
  caption_label: 'pl-3 flex flex-1 self-center text-[13px] sm:text-xl',
  day_selected: 'bg-green-soft bg-green rounded-full',
  cell: 'w-7 h-7 sm:w-8 sm:h-8 p-0.5',
  button: 'w-7 h-7 sm:w-8 sm:h-8 text-[13px] cursor-pointer',
};

export function BirthdayPicker({
  label,
  valid,
  error,
  horizontal,
  dob,
  disabled,
  onChange,
}: BithdayPickerProps) {
  const [active, toggleActive] = useToggle(false);
  const { inputProps, dayPickerProps } = useInput({
    defaultSelected: dob ? new Date(dob) : new Date(),
    fromYear: 2010,
    format: 'PP',
    required: true,
  });

  const pickerRef = useRef(null);
  useClickAway(pickerRef, () => {
    toggleActive(false);
  });

  const handleSelected = (date?: Date | DateRange) => {
     if (date) {
      if (isAfter(date as Date, new Date())) {
        alert("The date of birth is incorrect"); 
        return;
      }
      onChange(format(date as Date, 'yyyy-MM-dd'));
    }
    toggleActive(false);
  };

  return (
    <div className={clsx('flex flex-col', horizontal && 'sm:items-center')}>
      <div
        className={clsx('flex flex-col relative', horizontal && 'sm:flex-row')}
      >
        {label && (
          <>
            <label
              className={clsx('flex flex-1 mb-2', horizontal && 'sm:hidden')}
            >
              <h5>{label}</h5>
            </label>
            {horizontal && (
              <label className="hidden sm:block absolute top-5 -left-[200px] right-[288px] text-right">
                <h5>{label}</h5>
              </label>
            )}
          </>
        )}
        <div>
          <div
            className={clsx(
              'relative flex flex-col',
              horizontal && 'sm:max-w-[280px] sm:w-[280px]'
            )}
          >
            <input
              type="string"
              className={clsx(
                'relative text-body placeholder:text-gray-soft h-[60px] rounded-[10px] px-5 border border-gray-soft outline-none disabled:bg-gray-soft',
                valid && 'border-green',
                error && 'border-red'
              )}
              placeholder="DD.MM.YYYY"
              {...inputProps}
              onFocus={(e) => {
                toggleActive(true);
                inputProps.onFocus?.(e);
              }}
              disabled={disabled}
            />
            {valid && (
              <CheckIcon className="absolute w-5 h-5 right-5 top-5 text-green" />
            )}
          </div>
          {active && (
            <AnimatePresence>
              <motion.div
                ref={pickerRef}
                className="absolute -mt-[60px] max-w-[280px] w-full overflow-auto bg-white border border-t-0 border-gray-soft rounded-[10px] divide-y divide-gray-soft z-[10]"
              >
         
                <DayPicker
                  mode="single"
                  classNames={classNames}
                  onSelect={handleSelected}
                  {...dayPickerProps}
                />
              </motion.div>
            </AnimatePresence>
          )}
        </div>
      </div>
    </div>
  );
}
